import { c as _c } from "react/compiler-runtime";
import CheckBox from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import Clear from "@mui/icons-material/Clear";
import Close from "@mui/icons-material/Close";
import { Box, Button, Checkbox, Divider, FormControlLabel, IconButton, Popover, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { useSession } from "next-auth/react";
import React from "react";
import { getDifficultyColor } from "#/entities/difficulty/lib/getDifficultyColor";
import { difficultyLabels } from "#/entities/difficulty/model/difficultyLabels";
import { ProjectDifficulty } from "#/server/db/generated/enums";
import { useI18nContext } from "#/shared/hooks";
import { useProjectBrowserContext } from "./ProjectBrowserContext";
type ProjectBrowserFiltersProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
};
export const ProjectBrowserFilters: React.FC<ProjectBrowserFiltersProps> = t0 => {
  const $ = _c(65);
  const {
    anchorEl,
    open,
    onClose
  } = t0;
  const {
    LL
  } = useI18nContext();
  const theme = useTheme();
  const session = useSession();
  const {
    selectedDifficulties,
    showOnlyNew,
    setSelectedDifficulties,
    setShowOnlyNew,
    resetFilters
  } = useProjectBrowserContext();
  let t1;
  if ($[0] !== setSelectedDifficulties) {
    t1 = (_ev, newDifficulties) => {
      setSelectedDifficulties(newDifficulties);
    };
    $[0] = setSelectedDifficulties;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleDifficultyToggle = t1;
  let t2;
  if ($[2] !== setShowOnlyNew) {
    t2 = ev => {
      setShowOnlyNew(ev.target.checked);
    };
    $[2] = setShowOnlyNew;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleShowOnlyNewChange = t2;
  let t3;
  if ($[4] !== resetFilters) {
    t3 = () => {
      resetFilters();
    };
    $[4] = resetFilters;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleClearAll = t3;
  const hasActiveFilters = selectedDifficulties.length > 0 || showOnlyNew;
  let t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      vertical: "bottom",
      horizontal: "left"
    };
    t5 = {
      vertical: "top",
      horizontal: "left"
    };
    t6 = {
      paper: {
        sx: {
          mt: 1,
          minWidth: 280,
          maxWidth: 320,
          p: 2
        }
      }
    };
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== LL) {
    t7 = LL.FILTERS();
    $[9] = LL;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <Typography variant="h6" fontSize={16} fontWeight={600}>{t7}</Typography>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Close fontSize="small" />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== onClose) {
    t10 = <IconButton size="small" onClick={onClose} aria-label="Close filters">{t9}</IconButton>;
    $[14] = onClose;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <Stack direction="row" justifyContent="space-between" alignItems="center">{t8}{t10}</Stack>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Divider />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      mb: 1,
      fontWeight: 600
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== LL) {
    t14 = LL.FILTER_BY_DIFFICULTY();
    $[21] = LL;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <Typography variant="subtitle2" sx={t13}>{t14}</Typography>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      display: "flex",
      flexWrap: "wrap",
      gap: 0.5,
      "& .MuiToggleButton-root": {
        border: 1,
        borderColor: "divider",
        px: 1.5,
        py: 0.75,
        textTransform: "none",
        "&.Mui-selected": {
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          "&:hover": {
            backgroundColor: "primary.dark"
          }
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2
        }
      }
    };
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== selectedDifficulties || $[27] !== theme) {
    t17 = Object.values(ProjectDifficulty).map(difficulty => <ToggleButton key={difficulty} value={difficulty} aria-label={`Filter by ${difficultyLabels[difficulty]} difficulty`} sx={{
      color: selectedDifficulties.includes(difficulty) ? undefined : getDifficultyColor(theme, difficulty)
    }}>{difficultyLabels[difficulty]}</ToggleButton>);
    $[26] = selectedDifficulties;
    $[27] = theme;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== handleDifficultyToggle || $[30] !== selectedDifficulties || $[31] !== t17) {
    t18 = <ToggleButtonGroup value={selectedDifficulties} onChange={handleDifficultyToggle} aria-label="Difficulty filter" sx={t16}>{t17}</ToggleButtonGroup>;
    $[29] = handleDifficultyToggle;
    $[30] = selectedDifficulties;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t15 || $[34] !== t18) {
    t19 = <Box>{t15}{t18}</Box>;
    $[33] = t15;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Divider />;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  let t22;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <CheckBoxOutlineBlank />;
    t22 = <CheckBox />;
    $[37] = t21;
    $[38] = t22;
  } else {
    t21 = $[37];
    t22 = $[38];
  }
  let t23;
  if ($[39] !== handleShowOnlyNewChange || $[40] !== showOnlyNew) {
    t23 = <Checkbox checked={showOnlyNew} onChange={handleShowOnlyNewChange} icon={t21} checkedIcon={t22} />;
    $[39] = handleShowOnlyNewChange;
    $[40] = showOnlyNew;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== LL) {
    t24 = LL.SHOW_ONLY_NEW();
    $[42] = LL;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t23 || $[45] !== t24) {
    t25 = <Box><FormControlLabel control={t23} label={t24} /></Box>;
    $[44] = t23;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== LL || $[48] !== session.data) {
    t26 = session.data && <Box><FormControlLabel control={<Checkbox checked={false} disabled={true} icon={<CheckBoxOutlineBlank />} checkedIcon={<CheckBox />} />} label={LL.SHOW_ONLY_MINE()} /><Typography variant="caption" color="text.secondary" sx={{
        ml: 4
      }}>Coming soon</Typography></Box>;
    $[47] = LL;
    $[48] = session.data;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== LL || $[51] !== handleClearAll || $[52] !== hasActiveFilters) {
    t27 = hasActiveFilters && <><Divider /><Button startIcon={<Clear />} onClick={handleClearAll} variant="outlined" size="small" fullWidth={true}>{LL.CLEAR_ALL_FILTERS()}</Button></>;
    $[50] = LL;
    $[51] = handleClearAll;
    $[52] = hasActiveFilters;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t11 || $[55] !== t19 || $[56] !== t25 || $[57] !== t26 || $[58] !== t27) {
    t28 = <Stack spacing={2}>{t11}{t12}{t19}{t20}{t25}{t26}{t27}</Stack>;
    $[54] = t11;
    $[55] = t19;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== anchorEl || $[61] !== onClose || $[62] !== open || $[63] !== t28) {
    t29 = <Popover open={open} anchorEl={anchorEl} onClose={onClose} anchorOrigin={t4} transformOrigin={t5} slotProps={t6}>{t28}</Popover>;
    $[60] = anchorEl;
    $[61] = onClose;
    $[62] = open;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  return t29;
};